import { AxiosError } from 'axios';
import { axiosRequest } from './instance';
import { MentorId } from './mentors';
import { TenantId } from './tenants/Tenant';
import { VentureId } from './ventures/types/Venture';

// region MatchingRoundId
declare const _matchingRoundId: unique symbol;

export type MatchingRoundId = string & {
  [_matchingRoundId]: 'MatchingRoundId';
};
// endregion

export enum MatchingRoundStatus {
  CREATED = 'CREATED',
  PLANNED = 'PLANNED',
  IN_APPROVAL = 'IN_APPROVAL',
  FINALIZED = 'FINALIZED',
}

export interface MatchingRound {
  id: MatchingRoundId;
  tenantId: TenantId;
  initiatedBy: string;
  title: string;
  creationDate: string;
  description: string;
  isApprovalRequestSent: boolean;
  status: MatchingRoundStatus;
}

export interface MatchingRoundDetails {
  title: string;
  creationDate: string;
  description: string;
  status: MatchingRoundStatus;
}

export interface MatchingRoundVenture {
  id: string;
  matchingRoundId: MatchingRoundId;
  ventureId: VentureId;
}

export interface MatchRecommendation {
  id: string;
  mentorId: MentorId;
  mentorName: string;
  specialization: string;
  about: string;
  score: number;
  assigned: boolean;
  confirmedByFounder?: boolean;
  confirmedByMentor?: boolean;
}

export interface AdvancedMatchPage {
  id: string;
  ventureId: VentureId;
  ventureName: string;
  stage: string;
  description: string;
  recommendations: MatchRecommendation[];
  fulfilled?: boolean;
}

export interface ReverseMatchVenture {
  matchId: string;
  recommendationId: string;
  ventureId: VentureId;
  ventureName: string;
  stage: string;
  description: string;
  score: number;
  assigned: boolean;
  confirmedByFounder?: boolean;
  confirmedByMentor?: boolean;
}

/**
 * Raw API shape for the mentor-centric endpoint.
 * Each mentor has `matches[]` — one per venture — where each match contains
 * a full `recommendations` array (all mentors for that venture).
 */
interface ReverseMatchRaw {
  mentorId: MentorId;
  mentorFullName?: string;
  mentorName?: string;
  name?: string;
  fullName?: string;
  specialization?: string;
  about?: string;
  capacity?: number;
  // New API shape: ventures listed as "matches"
  matches?: Array<{
    id?: string;
    ventureId: VentureId;
    ventureName?: string;
    stage?: string;
    description?: string;
    finalized?: boolean;
    recommendations?: Array<{
      id?: string;
      mentorId: MentorId;
      weight?: number;
      score?: number;
      assigned?: boolean;
      confirmedByFounder?: boolean;
      confirmedByMentor?: boolean;
    }>;
  }>;
  // Legacy shape: flat ventures array
  ventures?: ReverseMatchVenture[];
}

export interface ReverseMatchRecommendation {
  mentorId: MentorId;
  mentorName: string;
  specialization: string;
  about: string;
  capacity: number;
  ventures: ReverseMatchVenture[];
}

function normalizeReverseMatch(
  raw: ReverseMatchRaw,
): ReverseMatchRecommendation {
  const mentorName =
    raw.mentorFullName || raw.mentorName || raw.name || raw.fullName || '';

  let ventures: ReverseMatchVenture[];

  if (raw.matches && raw.matches.length > 0) {
    // New API shape: extract ventures from matches, find this mentor's score
    ventures = raw.matches.map((match) => {
      const myRec = (match.recommendations ?? []).find(
        (r) => r.mentorId === raw.mentorId,
      );
      return {
        matchId: match.id ?? '',
        recommendationId: myRec?.id ?? '',
        ventureId: match.ventureId,
        ventureName: match.ventureName ?? '',
        stage: match.stage ?? '',
        description: match.description ?? '',
        score: myRec?.score ?? myRec?.weight ?? 0,
        assigned: myRec?.assigned ?? false,
        confirmedByFounder: myRec?.confirmedByFounder ?? false,
        confirmedByMentor: myRec?.confirmedByMentor ?? false,
      };
    });
  } else {
    // Legacy shape: flat ventures array
    ventures = (raw.ventures ?? []).map((v: any) => ({
      ...v,
      matchId: v.matchId ?? '',
      recommendationId: v.recommendationId ?? v.id ?? '',
      score: v.score ?? v.weight ?? 0,
      assigned: v.assigned ?? false,
      confirmedByFounder: v.confirmedByFounder ?? false,
      confirmedByMentor: v.confirmedByMentor ?? false,
    }));
  }

  return {
    mentorId: raw.mentorId,
    mentorName,
    specialization: raw.specialization || '',
    about: raw.about || '',
    capacity: raw.capacity ?? 5,
    ventures,
  };
}

export interface MatchLogFilterParams {
  search?: string;
  pageNumber?: number;
  pageSize?: number;
}

export interface MatchLog {
  id: string;
  creationDate: string;
  matchId: string;
  description: string;
}

export interface PublicMatchRecommendation {
  id: string;
  mentorId: MentorId;
  weight: number;
  creationDate: string;
  mentorName: string;
  confirmedByFounder: boolean;
  confirmedByMentor: boolean;
}

export interface PublicVentureMatch {
  id: string;
  tenantId: string;
  ventureId: string;
  creationDate: string;
  recommendations: PublicMatchRecommendation[];
  matchingRoundId: string;
  finalized: boolean;
  warning: boolean;
  warningType: string;
  matchWeights: Record<string, number>;
  ventureName: string;
  recommendationMentorIds: string[];
}

export interface PublicMentorMatchesResponse {
  mentorId: string;
  mentorFullName: string;
  matches: PublicVentureMatch[];
}

export interface CreateRecommendationRequest {
  mentorId: MentorId;
}


export interface TransferRecommendationRequest {
  sourceMatchId: string;
  recommendationId: string;
  destinationMatchId: string;
}

export interface ApproveMatchesBody {
  approved: string[];
  rejected: string[];
}

export interface MatchFilterParams {
  ventureName?: string;
  mentorName?: string;
  sortBy?: string;
  filteringMode?: string;
  pageNumber?: number;
  pageSize?: number;
}

export const SORT_BY_MAP: Record<string, string> = {
  name: 'NAME',
  assignedCount: 'ASSIGNED_COUNT',
  unassignedVentures: 'NAME',
  overloadedMentors: 'NAME',
};

export const SORT_FILTERING_MODE_MAP: Record<string, string | undefined> = {
  name: undefined,
  assignedCount: undefined,
  unassignedVentures: 'UNASSIGNED_VENTURES',
  overloadedMentors: 'OVERLOADED_MENTORS',
};

const TIMEOUT = 30000;

const matchingRoundsAPI = {
  async getMatchingRounds(page: number): Promise<MatchingRound[]> {
    try {
      const res = await axiosRequest.get<MatchingRound[]>({
        url: `/matching-rounds/filters/pages/${page}`,
        credentials: true,
        timeout: TIMEOUT,
      });
      return res.data;
    } catch (e: unknown) {
      const error = e as AxiosError;
      if (error.response?.status === 404) {
        return [];
      }
      console.error('Failed to fetch matching rounds:', error);
      throw error;
    }
  },

  async getAll(): Promise<MatchingRound[]> {
    try {
      const res = await axiosRequest.get<MatchingRound[]>({
        url: '/matching-rounds',
        credentials: true,
        timeout: TIMEOUT,
      });
      return res.data;
    } catch (e: unknown) {
      const error = e as AxiosError;
      if (error.response?.status === 404) {
        return [];
      }
      console.error('Failed to fetch all matching rounds:', error);
      throw error;
    }
  },

  async create(data: MatchingRoundDetails): Promise<MatchingRound> {
    try {
      const res = await axiosRequest.post<MatchingRound>({
        url: '/matching-rounds',
        credentials: true,
        data,
        timeout: TIMEOUT,
      });
      return res.data;
    } catch (e: unknown) {
      console.error('Failed to create matching round:', e);
      throw e;
    }
  },

  async update(data: MatchingRound): Promise<MatchingRound> {
    try {
      const res = await axiosRequest.put<MatchingRound>({
        url: '/matching-rounds',
        credentials: true,
        data,
        timeout: TIMEOUT,
      });
      return res.data;
    } catch (e: unknown) {
      console.error('Failed to update matching round:', e);
      throw e;
    }
  },

  async getVentures(roundId: MatchingRoundId): Promise<MatchingRoundVenture[]> {
    try {
      const res = await axiosRequest.get<MatchingRoundVenture[]>({
        url: `/matching-rounds/${roundId}/ventures`,
        credentials: true,
        timeout: TIMEOUT,
      });
      return res.data;
    } catch (e: unknown) {
      const error = e as AxiosError;
      if (error.response?.status === 404) {
        return [];
      }
      console.error('Failed to fetch matching round ventures:', error);
      throw error;
    }
  },

  async addVentures(
    roundId: MatchingRoundId,
    ventureIds: VentureId[],
  ): Promise<MatchingRoundVenture[]> {
    try {
      const res = await axiosRequest.post<MatchingRoundVenture[]>({
        url: `/matching-rounds/${roundId}/ventures`,
        credentials: true,
        data: ventureIds,
        timeout: TIMEOUT,
      });
      return res.data;
    } catch (e: unknown) {
      console.error('Failed to add ventures to matching round:', e);
      throw e;
    }
  },

  async removeVentures(
    roundId: MatchingRoundId,
    ventureIds: VentureId[],
  ): Promise<void> {
    try {
      await axiosRequest.delete<void>({
        url: `/matching-rounds/${roundId}/ventures`,
        credentials: true,
        data: ventureIds,
        timeout: TIMEOUT,
      });
    } catch (e: unknown) {
      console.error('Failed to remove ventures from matching round:', e);
      throw e;
    }
  },

  async simulateMatches(roundId: MatchingRoundId): Promise<unknown[]> {
    try {
      const res = await axiosRequest.post<unknown[]>({
        url: `/matching-rounds/${roundId}/advanced-matches/ventures/bulk`,
        credentials: true,
        timeout: 60000,
      });
      return res.data;
    } catch (e: unknown) {
      console.error('Failed to simulate matches:', e);
      throw e;
    }
  },

  async getMentorsForRound(
    roundId: MatchingRoundId,
  ): Promise<ReverseMatchRecommendation[]> {
    try {
      const res = await axiosRequest.get<ReverseMatchRaw[]>({
        url: `/matching-rounds/${roundId}/advanced-matches/mentors`,
        credentials: true,
        timeout: TIMEOUT,
      });
      return res.data.map(normalizeReverseMatch);
    } catch (e: unknown) {
      const error = e as AxiosError;
      if (error.response?.status === 404) {
        return [];
      }
      console.error('Failed to fetch mentors for round:', error);
      throw error;
    }
  },

  async transferRecommendation(
    data: TransferRecommendationRequest,
  ): Promise<unknown> {
    try {
      const res = await axiosRequest.post<unknown>({
        url: '/matching-rounds/advanced-matches/transfer',
        credentials: true,
        data,
        timeout: TIMEOUT,
      });
      return res.data;
    } catch (e: unknown) {
      console.error('Failed to transfer recommendation:', e);
      throw e;
    }
  },

  async removeRecommendation(
    matchId: string,
    recommendationId: string,
  ): Promise<void> {
    try {
      await axiosRequest.delete<void>({
        url: `/advanced-matches/${matchId}/recommendations/${recommendationId}`,
        credentials: true,
        timeout: TIMEOUT,
      });
    } catch (e: unknown) {
      console.error('Failed to remove recommendation:', e);
      throw e;
    }
  },

  async filterAdvancedMatches(
    roundId: MatchingRoundId,
    params: MatchFilterParams,
  ): Promise<AdvancedMatchPage[]> {
    try {
      const res = await axiosRequest.post<Array<Record<string, unknown>>>({
        url: `/matching-rounds/${roundId}/advanced-matches/filter`,
        credentials: true,
        data: params,
        timeout: TIMEOUT,
      });
      return res.data.map((item: Record<string, unknown>) => ({
        ...item,
        recommendations: (
          (item.recommendations as Array<Record<string, unknown>>) || []
        ).map((r: Record<string, unknown>) => ({
          ...r,
          score: (r.score as number) ?? (r.weight as number) ?? 0,
          assigned: (r.assigned as boolean) ?? false,
        })),
      })) as AdvancedMatchPage[];
    } catch (e: unknown) {
      const error = e as AxiosError;
      if (error.response?.status === 404) {
        return [];
      }
      console.error('Failed to filter advanced matches:', error);
      throw error;
    }
  },

  async filterMentors(
    roundId: MatchingRoundId,
    params: MatchFilterParams,
  ): Promise<ReverseMatchRecommendation[]> {
    try {
      const res = await axiosRequest.post<ReverseMatchRaw[]>({
        url: `/matching-rounds/${roundId}/advanced-matches/mentors/filter`,
        credentials: true,
        data: params,
        timeout: TIMEOUT,
      });
      return res.data.map(normalizeReverseMatch);
    } catch (e: unknown) {
      const error = e as AxiosError;
      if (error.response?.status === 404) {
        return [];
      }
      console.error('Failed to filter mentors:', error);
      throw error;
    }
  },

  async filterLogs(
    roundId: MatchingRoundId,
    params: MatchLogFilterParams,
  ): Promise<MatchLog[]> {
    try {
      const res = await axiosRequest.post<MatchLog[]>({
        url: `/matching-rounds/${roundId}/logs`,
        credentials: true,
        data: params,
        timeout: TIMEOUT,
      });
      return res.data;
    } catch (e: unknown) {
      const error = e as AxiosError;
      if (error.response?.status === 404) {
        return [];
      }
      console.error('Failed to fetch matching round logs:', error);
      throw error;
    }
  },

  async createRecommendation(
    matchId: string,
    data: CreateRecommendationRequest,
  ): Promise<MatchRecommendation> {
    try {
      const res = await axiosRequest.post<Record<string, unknown>>({
        url: `/advanced-matches/${matchId}/recommendations`,
        credentials: true,
        data,
        timeout: TIMEOUT,
      });
      const raw = res.data;
      return {
        id: raw.id as string,
        mentorId: raw.mentorId as MentorId,
        mentorName: '',
        specialization: '',
        about: '',
        score: (raw.weight as number) ?? 0,
        assigned: false,
      };
    } catch (e: unknown) {
      console.error('Failed to create recommendation:', e);
      throw e;
    }
  },

  async approveMentorMatches(
    tenantId: TenantId,
    roundId: MatchingRoundId,
    mentorId: MentorId,
    body: ApproveMatchesBody,
  ): Promise<void> {
    try {
      await axiosRequest.put<void>({
        url: `/public/tenants/${tenantId}/matching-rounds/${roundId}/mentors/${mentorId}/approve`,
        data: body,
        timeout: TIMEOUT,
      });
    } catch (e: unknown) {
      console.error('Failed to approve mentor matches:', e);
      throw e;
    }
  },

  async getPublicMentorMatches(
    tenantId: TenantId,
    roundId: MatchingRoundId,
    mentorId: MentorId,
  ): Promise<PublicMentorMatchesResponse[]> {
    try {
      const res = await axiosRequest.get<PublicMentorMatchesResponse[]>({
        url: `/public/tenants/${tenantId}/matching-rounds/${roundId}/mentors/${mentorId}`,
        timeout: TIMEOUT,
      });
      return res.data;
    } catch (e: unknown) {
      console.error('Failed to get public mentor matches:', e);
      throw e;
    }
  },
  async approveVentureMatch(
    tenantId: TenantId,
    roundId: MatchingRoundId,
    matchId: string,
    body: ApproveMatchesBody,
  ): Promise<void> {
    try {
      await axiosRequest.put<void>({
        url: `/public/tenants/${tenantId}/matching-rounds/${roundId}/matches/${matchId}/approve`,
        data: body,
        timeout: TIMEOUT,
      });
    } catch (e: unknown) {
      console.error('Failed to approve venture match:', e);
      throw e;
    }
  },

  async sendApprovals(roundId: MatchingRoundId): Promise<void> {
    try {
      await axiosRequest.post<void>({
        url: `/matching-rounds/${roundId}/approvals`,
        credentials: true,
        timeout: TIMEOUT,
      });
    } catch (e: unknown) {
      console.error('Failed to send approvals:', e);
      throw e;
    }
  },

  async getPublicVentureMatch(
    tenantId: TenantId,
    roundId: MatchingRoundId,
    matchId: string,
  ): Promise<PublicVentureMatch> {
    try {
      const res = await axiosRequest.get<PublicVentureMatch>({
        url: `/public/tenants/${tenantId}/matching-rounds/${roundId}/matches/${matchId}`,
        timeout: TIMEOUT,
      });
      return res.data;
    } catch (e: unknown) {
      console.error('Failed to get public venture match:', e);
      throw e;
    }
  },
};

export default matchingRoundsAPI;
